import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FaBox, FaClock, FaEye, FaShoppingCart, FaCreditCard, FaCheckCircle, FaPlus, FaComments } from 'react-icons/fa';

export default function UserDashboard() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes] = await Promise.all([
        axios.get(`${API}/user-listings/stats?user_id=${user.id}`)
      ]);
      
      setStats(statsRes.data);
      // setSubscription(subRes.data); // Subscription removed
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  const getSubscriptionBadge = () => {
    // Subscription removed
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Mon Tableau de Bord' : 'My Dashboard'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr' ? 'Bienvenue' : 'Welcome'}, {user?.name} !
          </p>
        </div>

        {/* Subscription Status - Removed */}
        {/* <Card className="mb-8 border-l-4 border-[#635BFF]">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold mb-2">
                  {i18n.language === 'fr' ? 'Statut de l\'abonnement' : 'Subscription Status'}
                </h3>
                <div className="flex items-center gap-3">
                  {getSubscriptionBadge()}
                  {subscription && subscription.end_date && (
                    <span className="text-sm text-gray-600">
                      {i18n.language === 'fr' ? 'Expire le' : 'Expires on'}: {new Date(subscription.end_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              {(!subscription || subscription.status === 'inactive') && (
                <Link to="/user/subscription">
                  <button className="btn-primary">
                    {i18n.language === 'fr' ? 'S\'abonner maintenant' : 'Subscribe Now'}
                  </button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card> */}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {i18n.language === 'fr' ? 'Total Annonces' : 'Total Listings'}
                  </p>
                  <p className="text-3xl font-bold text-[#0A2540]">{stats?.total_listings || 0}</p>
                </div>
                <FaBox className="text-4xl text-[#635BFF]" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                  </p>
                  <p className="text-3xl font-bold text-[#00D4FF]">{stats?.pending_listings || 0}</p>
                </div>
                <FaClock className="text-4xl text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {i18n.language === 'fr' ? 'Vues Totales' : 'Total Views'}
                  </p>
                  <p className="text-3xl font-bold text-[#0A2540]">{stats?.total_views || 0}</p>
                </div>
                <FaEye className="text-4xl text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {i18n.language === 'fr' ? 'Commandes' : 'Orders'}
                  </p>
                  <p className="text-3xl font-bold text-[#0A2540]">{stats?.total_orders || 0}</p>
                </div>
                <FaShoppingCart className="text-4xl text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Mon Annonce - For decouverte users */}
          {user?.roles?.includes('decouverte') && !user?.roles?.includes('supplier') && (
            <Link to="/user/my-annonce">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#635BFF]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-[#635BFF]">
                    <FaPlus />
                    {i18n.language === 'fr' ? 'Mon Annonce' : 'My Ad'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    {i18n.language === 'fr' 
                      ? 'Gérer votre annonce (Pack Découverte)' 
                      : 'Manage your ad (Discovery Pack)'}
                  </p>
                  <div className="mt-3">
                    <span className="inline-flex items-center gap-1 text-[#635BFF] text-sm font-medium">
                      <FaPlus className="text-xs" /> {i18n.language === 'fr' ? 'Créer ou modifier' : 'Create or edit'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Demandes de location - For decouverte users */}
          {user?.roles?.includes('decouverte') && (
            <Link to="/supplier/rental-requests">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#00D4FF]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-[#00D4FF]">
                    <FaClock />
                    {i18n.language === 'fr' ? 'Demandes de Location' : 'Rental Requests'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    {i18n.language === 'fr' 
                      ? 'Voir les demandes de location pour votre annonce' 
                      : 'View rental requests for your ad'}
                  </p>
                </CardContent>
              </Card>
            </Link>
          )}
          
          {/* Mes Produits - For suppliers only */}
          {(user?.roles?.includes('supplier') || user?.roles?.includes('admin')) && (
            <Link to="/supplier/my-products">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#635BFF]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-[#635BFF]">
                    <FaBox />
                    {i18n.language === 'fr' ? 'Mes Produits' : 'My Products'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    {i18n.language === 'fr' 
                      ? 'Gérer vos produits et créer une nouvelle annonce' 
                      : 'Manage your products and create a new listing'}
                  </p>
                  <div className="mt-3">
                    <span className="inline-flex items-center gap-1 text-[#635BFF] text-sm font-medium">
                      <FaPlus className="text-xs" /> {i18n.language === 'fr' ? 'Ajouter un produit' : 'Add a product'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          )}

          <Link to="/user/orders">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#0A2540]">
                  <FaShoppingCart />
                  {i18n.language === 'fr' ? 'Mes Commandes' : 'My Orders'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr' 
                    ? 'Voir l\'historique de vos achats' 
                    : 'View your purchase history'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/invoices">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#0A2540]">
                  <FaCreditCard />
                  {i18n.language === 'fr' ? 'Mes Factures' : 'My Invoices'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr' 
                    ? 'Télécharger vos factures' 
                    : 'Download your invoices'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/requests">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#0A2540]">
                  <FaClock />
                  {i18n.language === 'fr' ? 'Mes Demandes' : 'My Requests'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr' 
                    ? 'Gérer vos demandes de produits/services' 
                    : 'Manage your product/service requests'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/projects">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#635BFF]">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#635BFF]">
                  🏗️
                  {i18n.language === 'fr' ? 'Mes Projets de Chantier' : 'My Construction Projects'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr' 
                    ? 'Gérer vos projets et recevoir des devis avec analyse AI' 
                    : 'Manage projects and receive quotes with AI analysis'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/proposals">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#0A2540]">
                  <FaCheckCircle />
                  {i18n.language === 'fr' ? 'Mes Propositions' : 'My Proposals'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr'
                    ? 'Suivez vos demandes de devis et les réponses des fournisseurs'
                    : 'Track your quote requests and supplier replies'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/conversations">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#00D4FF]">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#00D4FF]">
                  <FaComments />
                  {i18n.language === 'fr' ? 'Mes Conversations' : 'My Conversations'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr'
                    ? 'Toutes vos conversations : produits, locations et demandes'
                    : 'All your conversations: products, rentals and requests'}
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/user/subscription">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 border-[#0A2540]">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-[#0A2540]">
                  💳
                  {i18n.language === 'fr' ? 'Mon Abonnement' : 'My Subscription'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm">
                  {i18n.language === 'fr'
                    ? 'Gérer votre formule et voir vos limites'
                    : 'Manage your plan and view your limits'}
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

      </div>
    </div>
  );
}
